/**
 * The `Group items by` dialog (spec M1-B4 §8.8, `[ui:library/table-13-group-by.png]`).
 *
 * Measured 533 x 212 at (529, 237): a column picker, a sorting-option picker (disabled until a
 * column is chosen), a `Show empty groups` checkbox (disabled likewise), `Clear` (disabled
 * until something is set) and `Save to this view`. Only columns whose UI declares `groupable`
 * are offered.
 */
import type { ColumnId } from '@elizmonday/shared';
import { getColumnUi } from '../../columns/registry';
import { useTable, useTableActions } from '../context';
import type { SortDirection } from '../settings';
import { Pane, PANE_BOX } from './Pane';
import { useSaveLabel } from './useSaveLabel';

export function GroupByPane() {
  const payload = useTable((state) => state.payload);
  const effective = useTable((state) => state.effective);
  const dirty = useTable((state) => state.dirty);
  const actions = useTableActions();
  const saveLabel = useSaveLabel();

  const groupBy = effective.groupBy;
  const columns = (payload?.columns ?? []).filter((column) => getColumnUi(column.type).groupable);

  return (
    <Pane
      testId="group-by-pane"
      title="Group items by"
      {...PANE_BOX.groupBy}
      dirty={dirty}
      canSave={payload?.permissions.canSaveView ?? false}
      saveLabel={saveLabel}
      onSave={() => void actions.saveView()}
      clearLabel="Clear"
      clearDisabled={groupBy === null}
      onClear={() => actions.setEffective({ groupBy: null })}
      onClose={() => actions.openPane(null)}
      footer={<span className="ez-board-pane__muted">Give feedback</span>}
    >
      <select
        aria-label="Select or search column name"
        data-testid="group-by-column"
        value={groupBy?.columnId ?? ''}
        onChange={(event) =>
          actions.setEffective({
            groupBy:
              event.target.value === ''
                ? null
                : {
                    columnId: event.target.value as ColumnId,
                    direction: groupBy?.direction ?? 'asc',
                    showEmptyGroups: groupBy?.showEmptyGroups ?? false,
                  },
          })
        }
      >
        <option value="">Select or search column name</option>
        {columns.map((column) => (
          <option key={column.id} value={column.id}>
            {column.title}
          </option>
        ))}
      </select>

      <select
        aria-label="Select sorting option"
        disabled={groupBy === null}
        value={groupBy?.direction ?? 'asc'}
        onChange={(event) =>
          groupBy &&
          actions.setEffective({ groupBy: { ...groupBy, direction: event.target.value as SortDirection } })
        }
      >
        <option value="asc">Ascending</option>
        <option value="desc">Descending</option>
      </select>

      <label className="ez-group-by__empty">
        <input
          type="checkbox"
          disabled={groupBy === null}
          checked={groupBy?.showEmptyGroups ?? false}
          onChange={(event) =>
            groupBy &&
            actions.setEffective({ groupBy: { ...groupBy, showEmptyGroups: event.target.checked } })
          }
        />
        Show empty groups
      </label>
    </Pane>
  );
}
